// Services
import { HttpService } from "@mgs/ci2019-common/src/services/HttpService";

// Store
import { Store } from "#/store";

// Model
import { ProductFilterCount } from "#/common/interfaces/conversionworld/ProductFilterCount";
import { Products } from "#/common/interfaces/conversionworld/Products";
import {
  BodyBuilderResponse,
  BodyBuilderRequest,
  BodyBuilder,
} from "#/common/interfaces/conversionworld/BodyBuilder";

// enums
import { Branche } from "../common/interfaces/conversionworld/Branche";
import { ListBody } from "../common/interfaces/ListBody";
import { Product } from "../common/interfaces/conversionworld/Product";
import { Pagination } from "#/common/interfaces/conversionworld/Pagination";
import { PopularSolution } from "../common/interfaces/conversionworld/PopularSolution";
import { ProductSearch } from "../common/interfaces/conversionworld/ProductSearch";
import {
  RequestFilter,
  RequestFilterWithPageId,
} from "../common/interfaces/conversionworld/RequestFilter";
import { Report } from "../common/interfaces/conversionworld/Report";

export class ConversionWorldService {
  private static _instance;

  static get instance() {
    if (!this._instance) {
      this._instance = new ConversionWorldService();
    }
    return this._instance;
  }

  private constructor() {}

  private readonly http: HttpService = HttpService.instance;

  public async getProductFilterCount(
    pageId: number
  ): Promise<ProductFilterCount> {
    let queryUrl =
      Store.instance.ui.conversionWorldApiUrl + `products/filter/count`;
    const response = await this.http.post<ProductFilterCount>(queryUrl, {
      pageId: pageId,
    });

    return response.body!;
  }

  async getProducts(
    filter: RequestFilterWithPageId,
    type: string,
    page?: number,
    epp?: number,
    ignoreRouteParameter?: boolean
  ): Promise<ListBody<Product, Pagination>> {
    let queryUrl = Store.instance.ui.conversionWorldApiUrl + `/products`;
    let routeParameter = Store.instance.page.page!.route_parameter;
    if (ignoreRouteParameter) {
      routeParameter = {};
    }

    const response = await this.http.post<ListBody<Product, Pagination>>(
      queryUrl,
      {
        filters: Object.assign({ route_parameter: routeParameter }, filter),
        pagination: {
          page: page || 0,
          epp: epp || 11,
        },
      },
      {
        params: { type },
      }
    );

    return response.body!;
  }

  async getFilters(requestBody: RequestFilterWithPageId): Promise<any> {
    let queryUrl = Store.instance.ui.conversionWorldApiUrl + `products/filter`;
    let routeParameter = Store.instance.page.page!.route_parameter;

    const response = await this.http.post<any>(
      queryUrl,
      Object.assign({ route_parameter: routeParameter }, requestBody)
    );
    return response.body!;
  }

  async getKonsiFilters(requestBody: RequestFilter): Promise<any> {
    let queryUrl = Store.instance.ui.conversionWorldApiUrl + `konsi/filter`;

    const response = await this.http.post<any>(queryUrl, requestBody);
    return response.body!;
  }

  async getProduct(id: number, pageId: number): Promise<Product> {
    let queryUrl = Store.instance.ui.conversionWorldApiUrl + `product/` + id;

    const urlParams = new URLSearchParams(window.location.search);
    const preview = urlParams.get("preview");

    const response = await this.http.get<Product>(queryUrl, {
      params: {
        page_id: pageId,
        preview,
      },
    });

    return response.body!;
  }

  getProductPdfLink(
    id: number,
    printCoverSheet: boolean,
    printLegalNoticeSheet: boolean
  ): string {
    let params = {
      cover: printCoverSheet ? "1" : "0",
      legalNotice: printLegalNoticeSheet ? "1" : "0",
    };
    let paramString = new URLSearchParams(params).toString();

    return (
      Store.instance.ui.conversionWorldApiUrl +
      `product/` +
      id +
      "/pdf?" +
      paramString
    );
  }

  /**
   * @method POST
   * @url /aufbauhersteller-liste/<type:string>
   */
  async getBodyBuilders(
    type: string | undefined,
    filter: RequestFilterWithPageId,
    pagination: Pagination
  ) {
    const res = await this.http.post<BodyBuilderResponse>(
      `${Store.instance.ui.conversionWorldApiUrl}aufbauhersteller-liste`,
      { filter, pagination },
      {
        params: {
          type: type,
        },
      }
    );
    return res.body as BodyBuilderResponse;
  }

  async getBodyBuilder(id, pageId): Promise<BodyBuilder> {
    const urlParams = new URLSearchParams(window.location.search);
    const preview = urlParams.get("preview");

    const res = await this.http.get(
      Store.instance.ui.conversionWorldApiUrl + "aufbauhersteller/" + id,
      {
        params: {
          page_id: pageId,
          preview,
        },
      }
    );
    return res.body as BodyBuilder;
  }

  async getBranchen(pageId: number): Promise<Branche[]> {
    const res = await this.http.get<Branche[]>(
      Store.instance.ui.conversionWorldApiUrl + "branchen",
      {
        params: {
          page_id: pageId,
        },
      }
    );

    return res.body!;
  }

  async getPopularSolution(pageId: number): Promise<PopularSolution> {
    const res = await this.http.get<PopularSolution>(
      Store.instance.ui.conversionWorldApiUrl + "popular-solution",
      {
        params: {
          page_id: pageId,
        },
      }
    );

    return res.body!;
  }

  async getReports(ids: string[], pageId: number): Promise<Report[]> {
    const res = await this.http.get<Report[]>(
      Store.instance.ui.conversionWorldApiUrl + "reports",
      {
        params: {
          ids: ids,
          page_id: pageId,
        },
      }
    );

    return res.body!;
  }

  async getKonsiProducts(
    filter: RequestFilter,
    page?: number,
    epp?: number
  ): Promise<ListBody<any, Pagination>> {
    let queryUrl = Store.instance.ui.conversionWorldApiUrl + `/konsi`;

    let body = {
      filters: filter,
      pagination: {
        page: page || 0,
        epp: epp || 3,
      },
    };

    const response = await this.http.post<ListBody<Product, Pagination>>(
      queryUrl,
      body
    );

    return response.body!;
  }

  async getKonsiLocation() {
    let queryUrl = Store.instance.ui.conversionWorldApiUrl + `/konsi/location`;
    const response = await this.http.post<object>(queryUrl);

    return response.body!;
  }
}
